import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsApi, cidadesApi, orcamentosApi, servicosApi, clientesApi } from '../lib/api';
import { formatDate, copyToClipboard, formatCurrency, generateWhatsAppMessageOrcamento, ORCAMENTO_STATUS_OPTIONS, getOrcamentoStatusColor } from '../lib/utils';
import { CidadeSelectOptions, ZonaBairroFields } from '../components/CidadeSelectOptions';
import { OrcamentoFormDialog } from '../components/OrcamentoFormDialog';
import useScrollRestoration from '../hooks/useScrollRestoration';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FileText, Phone, Clock, PhoneCall, XCircle, TrendingDown, CalendarClock, CalendarX, MessageCircle, CheckCircle2, RotateCcw } from 'lucide-react';

const LEAD_STATUS = ['Novo Lead', 'Em Atendimento', 'Aguardando Informações', 'Orçamento Enviado', 'Follow-up', 'Aprovado', 'Perdido'];
const LEAD_ORIGEM = ['Site', 'Instagram', 'WhatsApp', 'Indicação', 'Google', 'Outro'];
const LEAD_MOTIVOS = ['Sem resposta', 'Preço', 'Sem prestador disponível', 'Fora da região atendida', 'Cliente desistiu', 'Fechou com concorrente', 'Serviço não atendido pela Tenance', 'Outro'];

const STATUS_COLORS = {
  'Novo Lead': '#3B82F6',
  'Em Atendimento': '#8B5CF6',
  'Aguardando Informações': '#06B6D4',
  'Orçamento Enviado': '#F59E0B',
  'Follow-up': '#EAB308',
  'Aprovado': '#10B981',
  'Perdido': '#EF4444'
};

const nowKeySP = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 16).replace(' ', 'T');

const emptyLead = {
  nome: '',
  telefone: '',
  observacoes: '',
  origem: 'Outro',
  status: 'Novo Lead',
  cidade: '',
  zona: '',
  bairro: ''
};

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ ...emptyLead });
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('leads:statusFilter') || 'all');
  const [origemFilter, setOrigemFilter] = useState(() => sessionStorage.getItem('leads:origemFilter') || 'all');
  const [tentativasFilter, setTentativasFilter] = useState(() => sessionStorage.getItem('leads:tentativasFilter') || 'all');
  const setStatusFilterPersist = (v) => { sessionStorage.setItem('leads:statusFilter', v); setStatusFilter(v); };
  const setOrigemFilterPersist = (v) => { sessionStorage.setItem('leads:origemFilter', v); setOrigemFilter(v); };
  const setTentativasFilterPersist = (v) => { sessionStorage.setItem('leads:tentativasFilter', v); setTentativasFilter(v); };
  const [tab, setTab] = useState(() => sessionStorage.getItem('leads:tab') || 'ativos');
  const [motivosPerda, setMotivosPerda] = useState({ total: 0, itens: [] });
  const [cidadesList, setCidadesList] = useState([]);
  const [orcamentos, setOrcamentos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [orcDialog, setOrcDialog] = useState({ open: false, selected: null, prefill: null, leadId: null });
  const [fecharDialog, setFecharDialog] = useState({ open: false, orc: null });

  useEffect(() => {
    cidadesApi.list().then((r) => setCidadesList(r.data)).catch(() => {});
  }, []);
  useScrollRestoration(`leads:${tab}`, !loading);

  const setTabPersist = (t) => {
    sessionStorage.setItem('leads:tab', t);
    setTab(t);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (origemFilter !== 'all') params.origem = origemFilter;
      const [res, orcRes, servRes, cliRes] = await Promise.all([
        leadsApi.list(params),
        orcamentosApi.list(),
        servicosApi.list(),
        clientesApi.list(),
      ]);
      setLeads(res.data);
      setOrcamentos(orcRes.data);
      setServicos(servRes.data);
      setClientes(cliRes.data);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, origemFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadMotivosPerda = useCallback(async () => {
    try {
      const r = await leadsApi.relatorioMotivosPerda();
      setMotivosPerda(r.data);
    } catch (e) {
      console.error('Erro ao carregar motivos de perda', e);
    }
  }, []);

  useEffect(() => { loadMotivosPerda(); }, [loadMotivosPerda, leads]);

  const handleOpenForm = (lead = null) => {
    if (lead) {
      setSelected(lead);
      setFormData({
        nome: lead.nome,
        telefone: lead.telefone,
        observacoes: lead.observacoes || '',
        origem: lead.origem || 'Outro',
        status: lead.status || 'Novo',
        cidade: lead.cidade || '',
        zona: lead.zona || '',
        bairro: lead.bairro || ''
      });
    } else {
      setSelected(null);
      setFormData({ ...emptyLead });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelected(null);
    setFormData({ ...emptyLead });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.telefone.trim()) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (selected) {
        await leadsApi.update(selected.id, formData);
        toast.success('Lead atualizado!');
      } else {
        await leadsApi.create(formData);
        toast.success('Lead cadastrado!');
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Erro ao salvar lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await leadsApi.delete(selected.id);
      toast.success('Lead excluído!');
      setShowDeleteDialog(false);
      setSelected(null);
      loadData();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Erro ao excluir lead');
    }
  };

  const [motivoDialog, setMotivoDialog] = useState({ open: false, lead: null, orc: null, motivo: '', obs: '' });
  const [timelineDialog, setTimelineDialog] = useState({ open: false, lead: null });
  const [retornoDialog, setRetornoDialog] = useState({ open: false, lead: null, data: '', hora: '', observacao: '' });

  const handleStatusChange = async (lead, newStatus) => {
    if (newStatus === 'Perdido') {
      setMotivoDialog({ open: true, lead, orc: null, motivo: '', obs: '' });
      return;
    }
    try {
      await leadsApi.update(lead.id, { status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleConfirmMotivo = async () => {
    const { lead, orc, motivo, obs } = motivoDialog;
    if (!motivo) { toast.error('Selecione um motivo'); return; }
    if (motivo === 'Outro' && !obs.trim()) { toast.error('Descreva o motivo'); return; }
    try {
      if (orc) {
        await orcamentosApi.update(orc.id, { status: 'Negado' });
        if (lead) await leadsApi.update(lead.id, { status: 'Perdido', motivo_perda: motivo, motivo_perda_obs: obs });
        toast.success('Orçamento negado — motivo registrado');
      } else {
        await leadsApi.update(lead.id, { status: 'Perdido', motivo_perda: motivo, motivo_perda_obs: obs });
        toast.success('Lead marcado como Perdido');
      }
      setMotivoDialog({ open: false, lead: null, orc: null, motivo: '', obs: '' });
      loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao registrar motivo';
      toast.error(msg);
    }
  };

  const handleToggleVoucher = async (lead, checked) => {
    setLeads((prev) => prev.map((x) => (x.id === lead.id ? { ...x, voucher_enviado: checked } : x)));
    try {
      await leadsApi.update(lead.id, { voucher_enviado: checked });
      toast.success(checked ? 'Voucher marcado como enviado' : 'Marcação de voucher removida');
    } catch (err) {
      setLeads((prev) => prev.map((x) => (x.id === lead.id ? { ...x, voucher_enviado: !checked } : x)));
      toast.error('Erro ao atualizar voucher');
    }
  };

  const handleAddTentativa = async (lead) => {
    try {
      const r = await leadsApi.addTentativa(lead.id, '');
      const pc = r.data.proximo_contato;
      if (pc) {
        toast.success(`Tentativa #${r.data.total_tentativas} registrada — retorno ${formatDate(pc.data)} às ${pc.hora}`);
      } else {
        toast.success(`Tentativa #${r.data.total_tentativas} registrada`);
      }
      loadData();
    } catch (err) {
      toast.error('Erro ao registrar tentativa');
    }
  };

  const handleOpenRetorno = (lead) => {
    const pc = lead.proximo_contato || {};
    setRetornoDialog({
      open: true,
      lead,
      data: pc.data || '',
      hora: pc.hora || '',
      observacao: pc.observacao || '',
    });
  };

  const handleConfirmRetorno = async () => {
    const { lead, data, hora, observacao } = retornoDialog;
    if (!data) {
      toast.error('Informe a data do retorno');
      return;
    }
    try {
      await leadsApi.registrarContato(lead.id, {
        observacao: '',
        proximo_contato: { data, hora: hora || '', tipo: 'WhatsApp', observacao: observacao || '' },
      });
      toast.success('Retorno agendado');
      setRetornoDialog({ open: false, lead: null, data: '', hora: '', observacao: '' });
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao agendar retorno');
    }
  };

  const handleCancelarRetorno = async (lead) => {
    try {
      await leadsApi.registrarContato(lead.id, { observacao: '', proximo_contato: null });
      toast.success('Retorno cancelado');
      loadData();
    } catch (err) {
      toast.error('Erro ao cancelar retorno');
    }
  };

  const handleGerarOrcamento = (lead) => {
    const hoje = new Date();
    hoje.setDate(hoje.getDate() + 7);
    setOrcDialog({
      open: true,
      selected: null,
      leadId: lead.id,
      prefill: {
        cliente_nome: lead.nome,
        cliente_telefone: lead.telefone,
        cidade_atendimento: lead.cidade || '',
        zona_atendimento: lead.zona || '',
        bairro_atendimento: lead.bairro || '',
        observacoes: lead.observacoes || '',
        validade: hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })
      }
    });
  };

  const orcMap = useMemo(() => {
    const m = {};
    orcamentos.forEach((o) => { m[o.id] = o; });
    return m;
  }, [orcamentos]);

  const handleEditarOrcamento = (lead) => {
    const orc = orcMap[lead.orcamento_id];
    if (orc) setOrcDialog({ open: true, selected: orc, prefill: null, leadId: lead.id });
    else handleGerarOrcamento(lead);
  };

  const handleOrcStatusChange = async (orc, newStatus, lead = null) => {
    if (newStatus === 'Negado') {
      setMotivoDialog({ open: true, lead, orc, motivo: '', obs: '' });
      return;
    }
    try {
      await orcamentosApi.update(orc.id, { status: newStatus });
      if (newStatus === 'Expirado') toast.success('Orçamento atualizado — lead movido para Perdidos');
      else if (newStatus === 'Aprovado') toast.success('Orçamento aprovado — lead movido para Aprovados');
      else toast.success('Status do orçamento atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status do orçamento');
    }
  };

  const handleCopyWhatsAppOrc = async (orc) => {
    const ok = await copyToClipboard(generateWhatsAppMessageOrcamento(orc));
    if (ok) toast.success('Mensagem do orçamento copiada!');
  };

  const handleFecharConfirm = async () => {
    const orc = fecharDialog.orc;
    if (!orc) return;
    try {
      const res = await orcamentosApi.fechar(orc.id);
      setFecharDialog({ open: false, orc: null });
      navigate('/sistema/agendamento', {
        state: {
          fromOrcamento: {
            cliente_id: res.data.cliente_id,
            cliente_nome: res.data.cliente_nome,
            cliente_telefone: res.data.cliente_telefone,
            cliente_endereco: res.data.cliente_endereco,
            itens: res.data.itens,
            servico_id: res.data.servico_id,
            descricao_servico: res.data.descricao_servico,
            valor: res.data.valor,
            cidade_atendimento: res.data.cidade_atendimento,
            zona_atendimento: res.data.zona_atendimento || '',
            bairro_atendimento: res.data.bairro_atendimento || '',
            periodo_atendimento: res.data.periodo_atendimento,
            observacoes: res.data.observacoes,
            orcamento_id: res.data.orcamento_id
          }
        }
      });
    } catch (error) {
      console.error('Error closing orcamento:', error);
      toast.error('Erro ao fechar orçamento');
    }
  };

  const handleReabrirLead = async (lead) => {
    try {
      await leadsApi.reabrir(lead.id);
      toast.success(`${lead.nome} reaberto — voltou para Ativos`);
      loadData();
    } catch (error) {
      toast.error('Erro ao reabrir lead');
    }
  };

  const handleCopyTelefone = async (telefone) => {
    const ok = await copyToClipboard(telefone);
    if (ok) toast.success('Telefone copiado!');
  };

  // Tabs "Ativos" x "Orçamentos" x "Aprovados" x "Perdidos"
  const isPerdido = (l) => l.status === 'Perdido' || (l.tentativas?.length || 0) >= 4;
  const isAprovado = (l) => l.status === 'Aprovado' && !isPerdido(l);
  const isOrcamentoLead = (l) => !isPerdido(l) && !isAprovado(l) && (l.status === 'Orçamento Enviado' || !!l.orcamento_id);
  const visibleLeads = useMemo(() => {
    let filtered = leads.filter((l) => {
      if (tab === 'perdidos') return isPerdido(l);
      if (tab === 'aprovados') return isAprovado(l);
      if (tab === 'orcamentos') return isOrcamentoLead(l);
      return !isPerdido(l) && !isAprovado(l) && !isOrcamentoLead(l);
    });
    if (tentativasFilter !== 'all') {
      const n = parseInt(tentativasFilter, 10);
      filtered = filtered.filter((l) => {
        const t = l.tentativas?.length || 0;
        return n === 4 ? t >= 4 : t === n;
      });
    }
    if (tab === 'ativos') {
      const nowKey = nowKeySP(); // YYYY-MM-DDTHH:MM (Brasília)
      const isRetornoVencido = (l) => {
        const pc = l.proximo_contato;
        if (!pc || !pc.data) return false;
        const dt = `${pc.data}T${(pc.hora || '23:59').padStart(5, '0')}`;
        return dt <= nowKey;
      };
      // 1) Leads novos (sem tentativas e sem retorno) no topo — mais recente primeiro
      // 2) Retornos vencidos (mais antigo primeiro)
      // 3) Retornos agendados por horário ASC (08h, 11h, 14h, 16h30...)
      // 4) Com tentativas sem retorno: tentativas ASC, depois data ASC
      // 5) Aprovado sempre por último
      const retKey = (l) => `${l.proximo_contato.data}T${(l.proximo_contato.hora || '23:59').padStart(5, '0')}`;
      const grupo = (l) => {
        if (l.status === 'Aprovado') return 4;
        if ((l.tentativas?.length || 0) === 0 && !l.proximo_contato?.data) return 0;
        if (isRetornoVencido(l)) return 1;
        if (l.proximo_contato?.data) return 2;
        return 3;
      };
      return [...filtered].sort((a, b) => {
        const gA = grupo(a);
        const gB = grupo(b);
        if (gA !== gB) return gA - gB;
        if (gA === 0) return (b.created_at || '').localeCompare(a.created_at || '');
        if (gA === 1 || gA === 2) return retKey(a).localeCompare(retKey(b));
        const ta = a.tentativas?.length || 0;
        const tb = b.tentativas?.length || 0;
        if (ta !== tb) return ta - tb;
        return (a.created_at || '').slice(0, 10).localeCompare((b.created_at || '').slice(0, 10));
      });
    }
    if (tab === 'orcamentos') {
      // Vencidos no topo → retornos agendados por horário ASC → demais por validade
      const nowKey = nowKeySP();
      const isVencidoOrc = (l) => {
        const pc = l.proximo_contato;
        if (!pc || !pc.data) return false;
        return `${pc.data}T${(pc.hora || '23:59').padStart(5, '0')}` <= nowKey;
      };
      const retKeyOrc = (l) => `${l.proximo_contato.data}T${(l.proximo_contato.hora || '23:59').padStart(5, '0')}`;
      return [...filtered].sort((a, b) => {
        const gA = isVencidoOrc(a) ? 0 : (a.proximo_contato?.data ? 1 : 2);
        const gB = isVencidoOrc(b) ? 0 : (b.proximo_contato?.data ? 1 : 2);
        if (gA !== gB) return gA - gB;
        if (gA < 2) return retKeyOrc(a).localeCompare(retKeyOrc(b));
        const va = orcMap[a.orcamento_id]?.validade || '9999-12-31';
        const vb = orcMap[b.orcamento_id]?.validade || '9999-12-31';
        if (va !== vb) return va.localeCompare(vb);
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
    }
    return filtered;
  }, [leads, tab, tentativasFilter, orcMap]);
  const countsAtivos = leads.filter((l) => !isPerdido(l) && !isAprovado(l) && !isOrcamentoLead(l)).length;
  const countsOrcamentos = leads.filter(isOrcamentoLead).length;
  const countsAprovados = leads.filter(isAprovado).length;
  const countsPerdidos = leads.filter(isPerdido).length;

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Leads
          </h1>
          <p className="text-gray-500 mt-1">Captação e acompanhamento de novos contatos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setOrcDialog({ open: true, selected: null, prefill: null, leadId: null })}
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="add-orcamento-btn"
          >
            <Plus size={18} className="mr-2" /> Novo Orçamento
          </Button>
          <Button
            onClick={() => handleOpenForm()}
            className="text-white"
            style={{ backgroundColor: '#2B7A3D' }}
            data-testid="add-lead-btn"
          >
            <Plus size={18} className="mr-2" /> Novo Lead
          </Button>
        </div>
      </div>

      {/* Tabs Ativos / Perdidos */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTabPersist('ativos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'ativos' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-ativos"
        >
          Ativos <span className="ml-1 text-xs text-gray-400">({countsAtivos})</span>
        </button>
        <button
          type="button"
          onClick={() => setTabPersist('orcamentos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'orcamentos' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-orcamentos"
        >
          Orçamentos <span className="ml-1 text-xs text-gray-400">({countsOrcamentos})</span>
        </button>
        <button
          type="button"
          onClick={() => setTabPersist('aprovados')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'aprovados' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-aprovados"
        >
          Aprovados <span className="ml-1 text-xs text-gray-400">({countsAprovados})</span>
        </button>
        <button
          type="button"
          onClick={() => setTabPersist('perdidos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'perdidos' ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-perdidos"
        >
          Perdidos <span className="ml-1 text-xs text-gray-400">({countsPerdidos})</span>
        </button>
      </div>

      {tab === 'perdidos' && motivosPerda.total > 0 && (
        <Card data-testid="motivos-perda-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2 text-red-700">
              <TrendingDown size={18} /> Motivos de perda
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({motivosPerda.total} lead{motivosPerda.total > 1 ? 's' : ''} perdido{motivosPerda.total > 1 ? 's' : ''})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {motivosPerda.itens.map((m) => (
                <div key={m.motivo} className="flex items-center gap-3" data-testid={`motivo-${m.motivo}`}>
                  <div className="w-40 md:w-56 text-sm text-gray-700 truncate" title={m.motivo}>{m.motivo}</div>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 transition-all"
                      style={{ width: `${m.percentual}%` }}
                    />
                  </div>
                  <div className="w-24 text-right text-xs text-gray-600 font-medium tabular-nums">
                    {m.quantidade} · {m.percentual}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle style={{ fontFamily: 'Work Sans' }}>
              Lista de Leads
              <span className="ml-2 text-sm font-normal text-gray-500">({visibleLeads.length})</span>
            </CardTitle>
            <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
              <Select value={statusFilter} onValueChange={setStatusFilterPersist}>
                <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {LEAD_STATUS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={origemFilter} onValueChange={setOrigemFilterPersist}>
                <SelectTrigger className="w-40" data-testid="filter-origem"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as origens</SelectItem>
                  {LEAD_ORIGEM.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={tentativasFilter} onValueChange={setTentativasFilterPersist}>
                <SelectTrigger className="w-40" data-testid="filter-tentativas"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as tentativas</SelectItem>
                  <SelectItem value="0">0 tentativas</SelectItem>
                  <SelectItem value="1">1 tentativa</SelectItem>
                  <SelectItem value="2">2 tentativas</SelectItem>
                  <SelectItem value="3">3 tentativas</SelectItem>
                  <SelectItem value="4">4+ tentativas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : visibleLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum lead encontrado
            </div>
          ) : tab === 'orcamentos' ? (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th className="hidden lg:table-cell">Telefone</th>
                    <th>Serviço</th>
                    <th>Valor</th>
                    <th className="hidden lg:table-cell">Validade</th>
                    <th className="hidden lg:table-cell">Tentativas</th>
                    <th>Status do orçamento</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const orc = orcMap[l.orcamento_id];
                    const pc = l.proximo_contato;
                    const nowKey = nowKeySP();
                    const pcKey = pc && pc.data ? `${pc.data}T${(pc.hora || '23:59').padStart(5, '0')}` : '';
                    const vencido = pcKey && pcKey <= nowKey;
                    return (
                      <tr key={l.id} data-testid={`lead-orc-row-${l.id}`} className={vencido ? 'bg-red-50/60' : ''}>
                        <td className="font-medium">
                          <div className="flex flex-col">
                            <span>{l.nome}</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {pc && pc.data && (
                                <span
                                  className={`inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${vencido ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
                                  title={pc.observacao || ''}
                                  data-testid={`retorno-badge-orc-${l.id}`}
                                >
                                  <CalendarClock size={10} />
                                  {vencido ? 'Retornar agora' : `Retorno ${formatDate(pc.data)}${pc.hora ? ' às ' + pc.hora : ''}`}
                                </span>
                              )}                              {(l.tentativas?.length || 0) > 0 && (
                                <span className="lg:hidden inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                                  {l.tentativas.length}/4 tentativas
                                </span>
                              )}
                              {orc?.ordem_servico_id && (
                                <span className="inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">OS gerada</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="hidden lg:table-cell">
                          <button onClick={() => handleCopyTelefone(l.telefone)} className="inline-flex items-center gap-1 text-gray-700 hover:text-emerald-600" title="Copiar telefone">
                            <Phone size={13} /> {l.telefone}
                          </button>
                        </td>
                        <td className="cell-ellipsis" title={orc?.descricao_servico || ''}>
                          {orc?.descricao_servico || <span className="text-gray-400">Sem orçamento criado</span>}
                        </td>
                        <td className="font-medium text-emerald-700">{orc ? formatCurrency(orc.valor) : '—'}</td>
                        <td className="hidden lg:table-cell text-sm">{orc ? formatDate(orc.validade) : '—'}</td>
                        <td className="hidden lg:table-cell text-sm">
                          <span className={`px-2 py-1 rounded-full ${(l.tentativas?.length || 0) >= 4 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                            {l.tentativas?.length || 0}/4
                          </span>
                        </td>
                        <td>
                          {orc ? (
                            <Select value={orc.status} onValueChange={(v) => handleOrcStatusChange(orc, v, l)}>
                              <SelectTrigger
                                className="h-8 w-36 px-2 text-xs"
                                style={{ borderColor: getOrcamentoStatusColor(orc.status), color: getOrcamentoStatusColor(orc.status) }}
                                data-testid={`orc-status-select-${l.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ORCAMENTO_STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">{l.status}</span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1 flex-nowrap justify-end">
                            {orc ? (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleCopyWhatsAppOrc(orc)} title="Copiar mensagem do orçamento" data-testid={`whatsapp-orc-${l.id}`}>
                                  <MessageCircle size={16} className="text-green-600" />
                                </Button>
                                {orc.status !== 'Aprovado' && (
                                  <Button variant="ghost" size="sm" onClick={() => setFecharDialog({ open: true, orc })} title="Fechar e gerar OS" data-testid={`fechar-orc-${l.id}`}>
                                    <CheckCircle2 size={16} className="text-blue-600" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handleEditarOrcamento(l)} title="Editar orçamento" data-testid={`editar-orc-${l.id}`}>
                                  <Pencil size={16} className="text-amber-600" />
                                </Button>
                              </>
                            ) : (
                              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleGerarOrcamento(l)} data-testid={`criar-orc-${l.id}`}>
                                <FileText size={13} className="mr-1 text-blue-600" /> Criar orçamento
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setTimelineDialog({ open: true, lead: l })} title="Ver timeline" data-testid={`timeline-lead-${l.id}`}>
                              <Clock size={16} className="text-purple-600" />
                            </Button>
                            {l.status !== 'Aprovado' && l.status !== 'Perdido' && (
                              <Button variant="ghost" size="sm" onClick={() => handleOpenRetorno(l)} title={l.proximo_contato ? `Retorno agendado: ${l.proximo_contato.data}${l.proximo_contato.hora ? ' ' + l.proximo_contato.hora : ''}` : 'Agendar data/hora de retorno'} data-testid={`retorno-orc-${l.id}`}>
                                <CalendarClock size={16} className={l.proximo_contato ? 'text-emerald-600' : 'text-gray-500'} />
                              </Button>
                            )}
                            {!orc?.ordem_servico_id && (l.tentativas?.length || 0) < 4 && (
                              <Button variant="ghost" size="sm" onClick={() => handleAddTentativa(l)} title={`Registrar tentativa de contato (${l.tentativas?.length || 0}/4)`} data-testid={`tentativa-orc-${l.id}`}>
                                <PhoneCall size={16} className="text-amber-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleOpenForm(l)} title="Editar lead" data-testid={`edit-lead-${l.id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelected(l); setShowDeleteDialog(true); }} data-testid={`delete-lead-${l.id}`}>
                              <Trash2 size={16} className="text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th className="hidden lg:table-cell">Telefone</th>
                    <th className="hidden lg:table-cell">Origem</th>
                    <th className="hidden xl:table-cell">Observações</th>
                    <th className="hidden lg:table-cell">Recebido em</th>
                    <th className="hidden lg:table-cell">Tentativas</th>
                    {tab === 'perdidos' && <th className="text-center">Voucher enviado</th>}
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const pc = l.proximo_contato;
                    const nowKey = nowKeySP();
                    const pcKey = pc && pc.data ? `${pc.data}T${(pc.hora || '23:59').padStart(5, '0')}` : '';
                    const vencido = pcKey && pcKey <= nowKey;
                    return (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className={vencido ? 'bg-red-50/60' : ''}>
                      <td className="font-medium">
                        <div className="flex flex-col">
                          <span>{l.nome}</span>
                          {pc && pc.data && (
                            <span
                              className={`mt-1 inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${vencido ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
                              title={pc.observacao || ''}
                              data-testid={`retorno-badge-${l.id}`}
                            >
                              <CalendarClock size={10} />
                              {vencido ? 'Retornar agora' : `Retorno ${formatDate(pc.data)}${pc.hora ? ' às ' + pc.hora : ''}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell">
                        <button
                          onClick={() => handleCopyTelefone(l.telefone)}
                          className="inline-flex items-center gap-1 text-gray-700 hover:text-emerald-600"
                          title="Copiar telefone"
                        >
                          <Phone size={13} />
                          {l.telefone}
                        </button>
                      </td>
                      <td className="hidden lg:table-cell">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                          {l.origem}
                          {l.origem_publica && ' (site)'}
                        </span>
                      </td>
                      <td className="cell-ellipsis hidden xl:table-cell" title={l.observacoes}>
                        {l.cidade && (
                          <span className="block text-xs text-emerald-700 font-medium" data-testid={`lead-loc-${l.id}`}>
                            {[l.cidade, l.zona, l.bairro].filter(Boolean).join(' • ')}
                          </span>
                        )}
                        {l.observacoes || (!l.cidade && <span className="text-gray-400">—</span>)}
                      </td>
                      <td className="text-sm text-gray-600 whitespace-nowrap hidden lg:table-cell">
                        {l.created_at ? formatDate(l.created_at.slice(0, 10)) : ''}
                      </td>
                      <td className="hidden lg:table-cell text-xs">
                        <span className={`px-2 py-1 rounded-full ${(l.tentativas?.length || 0) >= 4 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {l.tentativas?.length || 0}/4
                        </span>
                      </td>
                      {tab === 'perdidos' && (
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={!!l.voucher_enviado}
                            onChange={(e) => handleToggleVoucher(l, e.target.checked)}
                            className="h-4 w-4 accent-emerald-700 cursor-pointer"
                            title={l.voucher_enviado ? 'Voucher enviado' : 'Marcar voucher enviado'}
                            data-testid={`voucher-check-${l.id}`}
                          />
                        </td>
                      )}
                      <td>
                        <Select value={l.status} onValueChange={(v) => handleStatusChange(l, v)}>
                          <SelectTrigger
                            className="h-8 w-36 px-2"
                            style={{ borderColor: STATUS_COLORS[l.status], color: STATUS_COLORS[l.status] }}
                            data-testid={`status-select-${l.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 flex-nowrap justify-end">
                          {tab === 'perdidos' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              onClick={() => handleReabrirLead(l)}
                              title="Reabrir lead e voltar para Ativos"
                              data-testid={`reabrir-lead-${l.id}`}
                            >
                              <RotateCcw size={13} className="mr-1" /> Reabrir
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTimelineDialog({ open: true, lead: l })}
                            title="Ver timeline"
                            data-testid={`timeline-lead-${l.id}`}
                          >
                            <Clock size={16} className="text-purple-600" />
                          </Button>
                          {l.status !== 'Aprovado' && l.status !== 'Perdido' && (l.tentativas?.length || 0) < 4 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAddTentativa(l)}
                              title="Registrar tentativa de contato"
                              data-testid={`tentativa-lead-${l.id}`}
                            >
                              <PhoneCall size={16} className="text-amber-600" />
                            </Button>
                          )}
                          {l.status !== 'Aprovado' && l.status !== 'Perdido' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenRetorno(l)}
                              title={l.proximo_contato ? `Retorno agendado: ${l.proximo_contato.data}${l.proximo_contato.hora ? ' ' + l.proximo_contato.hora : ''}` : 'Agendar data/hora de retorno'}
                              data-testid={`retorno-lead-${l.id}`}
                            >
                              <CalendarClock size={16} className={l.proximo_contato ? 'text-emerald-600' : 'text-gray-500'} />
                            </Button>
                          )}
                          {l.proximo_contato && l.status !== 'Aprovado' && l.status !== 'Perdido' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelarRetorno(l)}
                              title="Cancelar retorno agendado"
                              data-testid={`cancelar-retorno-${l.id}`}
                            >
                              <CalendarX size={16} className="text-gray-500 hover:text-red-600" />
                            </Button>
                          )}
                          {l.status !== 'Aprovado' && l.status !== 'Perdido' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGerarOrcamento(l)}
                              title="Gerar orçamento a partir do lead"
                              data-testid={`gerar-orcamento-${l.id}`}
                            >
                              <FileText size={16} className="text-blue-600" />
                            </Button>
                          )}
                          {l.orcamento_id && orcMap[l.orcamento_id] && (
                            <button
                              onClick={() => setOrcDialog({ open: true, selected: orcMap[l.orcamento_id], prefill: null, leadId: l.id })}
                              className={`text-xs px-2 py-1 rounded border ${l.status === 'Perdido' ? 'border-gray-400 text-gray-600 hover:bg-gray-50' : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'}`}
                              title={`Ver orçamento (${orcMap[l.orcamento_id].status})`}
                              data-testid={`goto-orcamento-${l.id}`}
                            >
                              Ver orçamento
                            </button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(l)}
                            data-testid={`edit-lead-${l.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelected(l); setShowDeleteDialog(true); }}
                            data-testid={`delete-lead-${l.id}`}
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agendar Retorno Dialog */}
      <Dialog open={retornoDialog.open} onOpenChange={(open) => !open && setRetornoDialog({ open: false, lead: null, data: '', hora: '', observacao: '' })}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock size={20} className="text-emerald-600" />
              Agendar retorno de contato
            </DialogTitle>
            <DialogDescription>
              {retornoDialog.lead?.nome} · {retornoDialog.lead?.telefone}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="retorno-data">Data *</Label>
                <Input
                  id="retorno-data"
                  type="date"
                  value={retornoDialog.data}
                  onChange={(e) => setRetornoDialog({ ...retornoDialog, data: e.target.value })}
                  data-testid="retorno-data-input"
                />
              </div>
              <div>
                <Label htmlFor="retorno-hora">Hora</Label>
                <Input
                  id="retorno-hora"
                  type="time"
                  value={retornoDialog.hora}
                  onChange={(e) => setRetornoDialog({ ...retornoDialog, hora: e.target.value })}
                  data-testid="retorno-hora-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="retorno-obs">Observação (opcional)</Label>
              <Textarea
                id="retorno-obs"
                rows={2}
                placeholder="Ex: Cliente pediu para retornar após 18h"
                value={retornoDialog.observacao}
                onChange={(e) => setRetornoDialog({ ...retornoDialog, observacao: e.target.value })}
                data-testid="retorno-obs-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetornoDialog({ open: false, lead: null, data: '', hora: '', observacao: '' })}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmRetorno} className="text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="confirmar-retorno-btn">
              Agendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Motivo da Perda Dialog */}
      <Dialog open={motivoDialog.open} onOpenChange={(open) => !open && setMotivoDialog({ open: false, lead: null, orc: null, motivo: '', obs: '' })}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={20} className="text-red-500" />
              Motivo da perda
            </DialogTitle>
            <DialogDescription>
              {motivoDialog.orc ? 'Selecione por que este orçamento foi negado — obrigatório para relatórios.' : 'Selecione por que este lead foi perdido — obrigatório para relatórios.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Motivo *</Label>
              <Select value={motivoDialog.motivo} onValueChange={(v) => setMotivoDialog((p) => ({ ...p, motivo: v }))}>
                <SelectTrigger data-testid="motivo-perda-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {LEAD_MOTIVOS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {motivoDialog.motivo === 'Outro' && (
              <div className="space-y-1">
                <Label>Descreva o motivo *</Label>
                <Textarea
                  value={motivoDialog.obs}
                  onChange={(e) => setMotivoDialog((p) => ({ ...p, obs: e.target.value }))}
                  rows={3}
                  data-testid="motivo-perda-obs"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMotivoDialog({ open: false, lead: null, orc: null, motivo: '', obs: '' })}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmMotivo}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-motivo-perda-btn"
            >
              {motivoDialog.orc ? 'Negar orçamento' : 'Marcar como Perdido'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={timelineDialog.open} onOpenChange={(open) => !open && setTimelineDialog({ open: false, lead: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={20} className="text-purple-600" />
              Timeline · {timelineDialog.lead?.nome}
            </DialogTitle>
            <DialogDescription>
              Histórico automático de todas as ações do lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(!timelineDialog.lead?.timeline || timelineDialog.lead.timeline.length === 0) ? (
              <p className="text-sm text-gray-500 text-center py-4">Sem eventos ainda</p>
            ) : (
              [...timelineDialog.lead.timeline].reverse().map((ev, i) => (
                <div key={i} className="flex gap-3 border-l-2 border-purple-200 pl-3 py-1">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">
                      {ev.data ? new Date(ev.data).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : ''}
                      {ev.autor && ` · ${ev.autor}`}
                    </p>
                    <p className="text-sm font-medium">{ev.descricao}</p>
                  </div>
                </div>
              ))
            )}
            {timelineDialog.lead && (timelineDialog.lead.tentativas?.length || 0) > 0 && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Tentativas de contato</p>
                {timelineDialog.lead.tentativas.map((t, i) => (
                  <p key={i} className="text-xs text-gray-600">
                    #{i + 1} · {new Date(t.data).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    {t.observacao && ` — ${t.observacao}`}
                  </p>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => (!open ? handleCloseForm() : null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selected ? 'Editar Lead' : 'Novo Lead'}
            </DialogTitle>
            <DialogDescription>
              Cadastre o contato inicial. Depois você pode converter em orçamento.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                data-testid="lead-nome-input"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="telefone">Telefone *</Label>
              <Input
                id="telefone"
                value={formData.telefone}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                required
                placeholder="(31) 9 9999-9999"
                data-testid="lead-telefone-input"
              />
            </div>

            <div className="space-y-1">
              <Label>Cidade</Label>
              <Select
                value={formData.cidade || undefined}
                onValueChange={(v) => setFormData({ ...formData, cidade: v, zona: '' })}
              >
                <SelectTrigger data-testid="lead-cidade-select">
                  <SelectValue placeholder="Selecione a cidade (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <CidadeSelectOptions cidades={cidadesList} currentValue={formData.cidade} />
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ZonaBairroFields
                cidades={cidadesList}
                cidade={formData.cidade}
                zona={formData.zona}
                bairro={formData.bairro}
                onZonaChange={(v) => setFormData({ ...formData, zona: v })}
                onBairroChange={(v) => setFormData({ ...formData, bairro: v })}
                testPrefix="lead"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="origem">Origem</Label>
                <Select
                  value={formData.origem}
                  onValueChange={(v) => setFormData({ ...formData, origem: v })}
                >
                  <SelectTrigger data-testid="lead-origem-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_ORIGEM.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
                placeholder="O que o cliente está buscando, contexto etc."
                data-testid="lead-observacoes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="text-white"
                style={{ backgroundColor: '#2B7A3D' }}
                data-testid="save-lead-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Orçamento: criar/editar dentro de Leads */}
      <OrcamentoFormDialog
        open={orcDialog.open}
        onOpenChange={(v) => setOrcDialog((p) => ({ ...p, open: v }))}
        selected={orcDialog.selected}
        prefill={orcDialog.prefill}
        leadId={orcDialog.leadId}
        servicos={servicos}
        cidadesList={cidadesList}
        clientes={clientes}
        onSaved={() => { setTabPersist('orcamentos'); loadData(); }}
      />

      {/* Fechar orçamento (gerar OS) */}
      <AlertDialog open={fecharDialog.open} onOpenChange={(v) => !v && setFecharDialog({ open: false, orc: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar orçamento e gerar OS?</AlertDialogTitle>
            <AlertDialogDescription>
              O cliente será cadastrado (caso ainda não exista) e você será levado ao formulário de Ordem de Serviço já preenchido.
              Após salvar a OS, o orçamento é marcado como Aprovado e o lead vai automaticamente para a aba Aprovados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleFecharConfirm} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-fechar-orcamento-btn">
              Continuar para OS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este lead? Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-lead-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Leads;
